import { format, parse } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

const ISO_DATE_LENGTH = 10;

// Truncates to the date portion without going through a Date when the value is
// already a string, so a date-only value keeps its calendar day regardless of
// the viewer's time zone.
export const toIsoDateString = (value: Date | string): string =>
    typeof value === 'string'
        ? value.slice(0, ISO_DATE_LENGTH)
        : value.toISOString().slice(0, ISO_DATE_LENGTH);

const DATE_PART_PAD_LENGTH = 2;

const DATE_PART_PAD_CHARACTER = '0';

const DATE_PART_SEPARATOR = '-';

const MONTH_INDEX_OFFSET = 1;

// Built from the local calendar fields rather than toISOString, so the result is
// the viewer's today rather than UTC's.
export const getTodayIsoDateString = (): string => {
    const today = new Date();

    const month = String(today.getMonth() + MONTH_INDEX_OFFSET).padStart(
        DATE_PART_PAD_LENGTH,
        DATE_PART_PAD_CHARACTER
    );

    const dayOfMonth = String(today.getDate()).padStart(
        DATE_PART_PAD_LENGTH,
        DATE_PART_PAD_CHARACTER
    );

    return [String(today.getFullYear()), month, dayOfMonth].join(DATE_PART_SEPARATOR);
};

// Compared as date-only strings rather than Dates, so a value expiring today is
// not flagged early for viewers west of UTC. A value with no expiration date is
// never expired.
export const getIsExpired = (expirationDate?: Date | string | null): boolean =>
    expirationDate ? toIsoDateString(expirationDate) < getTodayIsoDateString() : false;

// Compared as date-only strings for the same reason as getIsExpired. A missing
// value is never in the future, so optional date fields stay optional.
export const getIsFutureDate = (value?: Date | string | null): boolean =>
    value ? toIsoDateString(value) > getTodayIsoDateString() : false;

// Compared as date-only strings for the same reason as getIsExpired. Either
// value missing means there is nothing to compare, so the pair is never flagged
// and both fields stay independently optional. An equal pair is not "before",
// so a value issued and expiring the same day is allowed.
export const getIsDateBeforeDate = (
    value?: Date | string | null,
    comparisonValue?: Date | string | null
): boolean =>
    value && comparisonValue ? toIsoDateString(value) < toIsoDateString(comparisonValue) : false;

export const constructUtcDateTime = (
    date: Date,
    time: string,
    timeZone: string,
    referenceDate: Date
) => {
    const dateStr = format(date, 'yyyy-MM-dd');

    if (!time) {
        return zonedTimeToUtc(dateStr, timeZone);
    }

    const dateTimeStr = `${dateStr} ${time}`;
    let formatString = 'yyyy-MM-dd HH:mm';

    // account for seconds if needed
    if (time.length === 8) {
        formatString = 'yyyy-MM-dd HH:mm:ss';
    }
    const zonedDate = parse(dateTimeStr.trim(), formatString, referenceDate);

    return zonedTimeToUtc(zonedDate, timeZone);
};
